import { query } from "../../erd-editor-schema/dist/query/index.js";
import { ColumnOption, ColumnType } from "../../constants/schema.js";
import { arrayHas } from "../arrayHas.js";
import { getLiveDomainName } from "../../engine/modules/dictionary/membership.js";
import { bHas } from "../bit.js";
import { SelectType } from "../../engine/modules/editor/state.js";
import { drawsDomainInTypeCell } from "../../engine/modules/editor/utils/nameView.js";
import { CLIPBOARD_HTML_ATTR, CLIPBOARD_HTML_TRUNCATED_ATTR, PayloadKind, createPayload } from "./payload.js";
import { CellType, getShowColumnOrder } from "./index.js";
//#region src/utils/table-clipboard/copy.ts
function tableCopyToText(state) {
	return getTableData(state).map((row) => row.map(([, value]) => value).join("	")).join("\n");
}
function tableCopyToHtml(state) {
	return rowsToHtmlTable(getTableData(state));
}
function getTableData(state) {
	const { editor: { focusTable }, settings: { show, columnOrder, nameViewMode }, collections } = state;
	const rows = [];
	if (!focusTable || focusTable.edit || focusTable.selectColumnIds.length === 0) return rows;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return rows;
	const hasColumnIds = arrayHas(focusTable.selectColumnIds);
	const columns = query(collections).collection("tableColumnEntities").selectByIds(table.columnIds).filter((column) => hasColumnIds(column.id));
	const showColumnOrder = getShowColumnOrder(show, columnOrder, nameViewMode);
	return columns.map((column) => showColumnOrder.map((columnType) => toCell(state, column, columnType)));
}
function entitiesCopyToPayload(state) {
	const { editor: { selectedMap }, collections } = state;
	const tableIds = [];
	const memoIds = [];
	for (const [id, selectType] of Object.entries(selectedMap)) if (selectType === SelectType.table) tableIds.push(id);
	else if (selectType === SelectType.memo) memoIds.push(id);
	const tables = query(collections).collection("tableEntities").selectByIds(tableIds);
	const memos = query(collections).collection("memoEntities").selectByIds(memoIds);
	if (tables.length === 0 && memos.length === 0) return null;
	const columns = tables.flatMap((table) => query(collections).collection("tableColumnEntities").selectByIds(table.columnIds));
	const copiedTableIds = tables.map(({ id }) => id);
	return createPayload({
		kind: PayloadKind.tables,
		tables: tables.map(toClipboardTable),
		columns: columns.map(toClipboardColumn),
		memos: memos.map(toClipboardMemo),
		relationships: toClipboardRelationships(state, copiedTableIds),
		indexes: toClipboardIndexes(state, copiedTableIds)
	});
}
function toClipboardRelationships({ doc, collections }, tableIds) {
	const hasTableId = arrayHas(tableIds);
	return query(collections).collection("relationshipEntities").selectByIds(doc.relationshipIds).filter(({ start, end }) => hasTableId(start.tableId) && hasTableId(end.tableId)).map(({ relationshipType, onDelete, onUpdate, start, end }) => ({
		relationshipType,
		onDelete,
		onUpdate,
		start: {
			tableId: start.tableId,
			columnIds: [...start.columnIds]
		},
		end: {
			tableId: end.tableId,
			columnIds: [...end.columnIds]
		}
	}));
}
function toClipboardIndexes({ doc, collections }, tableIds) {
	const hasTableId = arrayHas(tableIds);
	const indexColumnCollection = query(collections).collection("indexColumnEntities");
	const clipboardIndexes = [];
	for (const index of query(collections).collection("indexEntities").selectByIds(doc.indexIds)) {
		if (!hasTableId(index.tableId)) continue;
		const indexColumns = [];
		let resolved = true;
		for (const indexColumnId of index.indexColumnIds) {
			const indexColumn = indexColumnCollection.selectById(indexColumnId);
			if (!indexColumn) {
				resolved = false;
				break;
			}
			indexColumns.push({
				columnId: indexColumn.columnId,
				orderType: indexColumn.orderType
			});
		}
		if (!resolved) continue;
		clipboardIndexes.push({
			tableId: index.tableId,
			name: index.name,
			unique: index.unique,
			indexColumns
		});
	}
	return clipboardIndexes;
}
function columnsCopyToPayload({ editor: { focusTable }, collections }) {
	if (!focusTable || focusTable.edit || focusTable.selectColumnIds.length === 0) return null;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	const hasColumnIds = arrayHas(focusTable.selectColumnIds);
	const columns = table ? query(collections).collection("tableColumnEntities").selectByIds(table.columnIds).filter((column) => hasColumnIds(column.id)) : [];
	return createPayload({
		kind: PayloadKind.columns,
		columns: columns.map(toClipboardColumn)
	});
}
function entitiesToTsv(payload, state) {
	return payload.tables.length === 0 ? payload.memos.map(({ value }) => value).join("\n\n") : toEntityRows(payload, state).map((row) => row.map(([, value]) => value).join("	")).join("\n");
}
function entitiesToHtmlTable(payload, state) {
	return rowsToHtmlTable(toEntityRows(payload, state));
}
function payloadToHtml(payload, tableHtml) {
	const json = escapeHtml(JSON.stringify(payload));
	return byteLength(json) > 1e6 ? `<span ${CLIPBOARD_HTML_TRUNCATED_ATTR}="1">${tableHtml}</span>` : `<span ${CLIPBOARD_HTML_ATTR}="${json}">${tableHtml}</span>`;
}
function toClipboardTable(table) {
	return {
		sourceId: table.id,
		name: table.name,
		logicalName: table.logicalName,
		comment: table.comment,
		columnIds: [...table.columnIds],
		nameDerived: table.nameDerived,
		ui: {
			x: table.ui.x,
			y: table.ui.y,
			zIndex: table.ui.zIndex,
			widthName: table.ui.widthName,
			widthComment: table.ui.widthComment,
			color: table.ui.color
		}
	};
}
function toClipboardColumn(column) {
	return {
		sourceId: column.id,
		tableId: column.tableId,
		name: column.name,
		comment: column.comment,
		dataType: column.dataType,
		default: column.default,
		options: column.options,
		logicalName: column.logicalName,
		domainId: column.domainId,
		ui: {
			keys: column.ui.keys,
			widthName: column.ui.widthName,
			widthComment: column.ui.widthComment,
			widthDataType: column.ui.widthDataType,
			widthDefault: column.ui.widthDefault
		}
	};
}
function toClipboardMemo(memo) {
	return {
		sourceId: memo.id,
		value: memo.value,
		ui: {
			x: memo.ui.x,
			y: memo.ui.y,
			width: memo.ui.width,
			height: memo.ui.height,
			zIndex: memo.ui.zIndex,
			color: memo.ui.color
		}
	};
}
function rowsToHtmlTable(rows) {
	if (rows.length === 0) return "";
	return `<table><tbody>${rows.map((row) => `<tr>${row.map(cellToHtml).join("")}</tr>`).join("")}</tbody></table>`;
}
function cellToHtml([type, value]) {
	return `<td data-type="${escapeHtml(type)}">${escapeHtml(value)}</td>`;
}
function toEntityRows(payload, state) {
	const { show, columnOrder, nameViewMode } = state.settings;
	const showColumnOrder = getShowColumnOrder(show, columnOrder, nameViewMode);
	const columnMap = new Map(payload.columns.map((column) => [column.sourceId, column]));
	return payload.tables.flatMap((table) => table.columnIds.map((columnId) => columnMap.get(columnId)).filter((column) => Boolean(column)).map((column) => showColumnOrder.map((columnType) => toCell(state, column, columnType))));
}
function toCell(state, column, columnType) {
	const domainId = column.domainId ?? "";
	switch (columnType) {
		case ColumnType.columnName: return [CellType.columnName, column.name];
		case ColumnType.columnLogicalName: return [CellType.columnLogicalName, column.logicalName ?? ""];
		case ColumnType.columnDataType: return drawsDomainInTypeCell(state, { domainId }) ? [CellType.columnDomain, getLiveDomainName(state, domainId)] : [CellType.columnDataType, column.dataType];
		case ColumnType.columnDomain: return [CellType.columnDomain, getLiveDomainName(state, domainId)];
		case ColumnType.columnDefault: return [CellType.columnDefault, column.default];
		case ColumnType.columnComment: return [CellType.columnComment, column.comment];
		case ColumnType.columnAutoIncrement: return [CellType.columnAutoIncrement, bHas(column.options, ColumnOption.autoIncrement) ? "TRUE" : "FALSE"];
		case ColumnType.columnUnique: return [CellType.columnUnique, bHas(column.options, ColumnOption.unique) ? "TRUE" : "FALSE"];
		case ColumnType.columnNotNull: return [CellType.columnNotNull, bHas(column.options, ColumnOption.notNull) ? "NOT NULL" : "NULL"];
		default: return ["", ""];
	}
}
var HTML_ESCAPE_MAP = {
	"&": "&amp;",
	"<": "&lt;",
	">": "&gt;",
	"\"": "&quot;",
	"'": "&#39;"
};
function escapeHtml(value) {
	return value.replace(/[&<>"']/g, (char) => HTML_ESCAPE_MAP[char]);
}
var textEncoder = new TextEncoder();
function byteLength(value) {
	return textEncoder.encode(value).length;
}
//#endregion
export { columnsCopyToPayload, entitiesCopyToPayload, entitiesToHtmlTable, entitiesToTsv, payloadToHtml, tableCopyToHtml, tableCopyToText, toClipboardIndexes, toClipboardRelationships };
